import { useParams, useNavigate } from "react-router-dom";
import { useDeleteRecallCard, useRecallCard, useUpdateRecallCard } from "./hooks/useRecall";
import { useAuth } from "../authentication/AuthContext";
import Header from "../components/Header";
import { ArrowLeft, Brain, Tag, BarChart2, Calendar, FolderOpen, Trash2 } from "lucide-react";
import CodeBlock from "./components/CodeBlock";
import { useFolders } from "../folder/hooks/useFolder";
import AddRecallCardModal from "./components/AddRecallCardModal";
import { useState } from "react";
import { sortFolders } from "../folder/utils/folders";
import { recallTimestamp } from "./recallScheduling";

const RECALL_SCOPE = "RECALL";

const formatNextReview = (timestamp) => {
    const nextReviewAt = recallTimestamp(timestamp);
    if (nextReviewAt === null) return "—";

    const remainingMs = nextReviewAt - Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const relativeTime = new Intl.RelativeTimeFormat(undefined, { numeric: "always" });

    // Allow for the few milliseconds elapsed between scheduling and rendering.
    if (remainingMs >= dayMs - 60_000) {
        const days = Math.max(1, Math.round(remainingMs / dayMs));
        return relativeTime.format(days, "day");
    }

    if (remainingMs > 0) {
        const minutes = Math.max(1, Math.ceil(remainingMs / 60_000));
        return relativeTime.format(minutes, "minute");
    }

    return new Date(nextReviewAt).toLocaleString([], {
        dateStyle: "medium",
        timeStyle: "short",
    });
};

export const RecallCardDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { authToken } = useAuth();
    const { data: card, isLoading } = useRecallCard(authToken, id);
    const { data: folders = [], isLoading: areFoldersLoading } = useFolders(authToken, RECALL_SCOPE);
    const { mutate: updateCard, isLoading: isUpdating } = useUpdateRecallCard(authToken);
    const { mutate: deleteCard, isLoading: isDeleting, error: deleteError } = useDeleteRecallCard(authToken);
    const [showEditModal, setShowEditModal] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const sortedFolders = sortFolders(folders);

    const handleDelete = () => {
        deleteCard(id, { onSuccess: () => navigate("/recall") });
    };

    const handleFolderChange = (event) => {
        updateCard({
            recallCardId: id,
            cardData: {
                ...card,
                parentFolderId: event.target.value || null,
                parent_folder_id: event.target.value || null,
            },
        });
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#12131a] flex items-center justify-center">
                <div className="text-gray-400 text-sm animate-pulse">Loading card...</div>
            </div>
        );
    }

    if (!card) {
        return (
            <div className="min-h-screen bg-[#12131a] flex items-center justify-center">
                <div className="text-gray-500 text-sm">Card not found.</div>
            </div>
        );
    }

    const difficultyLabel = typeof card.difficulty === "number"
        ? card.difficulty.toFixed(2)
        : "—";

    const stateColor = {
        NEW: "text-blue-400 bg-blue-400/10",
        LEARNING: "text-orange-400 bg-orange-400/10",
        REVIEW: "text-purple-400 bg-purple-400/10",
        MASTERED: "text-green-400 bg-green-400/10",
    }[card.learning_state] ?? "text-gray-400 bg-gray-700";
    return (
        <div className="min-h-screen bg-[#12131a]">
            <Header />
            <div className="max-w-2xl mx-auto px-6 pt-24 pb-16">

                {/* Back */}
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200 transition mb-8"
                >
                    <ArrowLeft size={16} />
                    Back to cards
                </button>

                {/* Front card */}
                <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-2xl p-6 mb-4 shadow-lg">
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                        <Brain size={13} />
                        <span>FRONT</span>
                    </div>
                    <CodeBlock text={card.front} lang={card.lang ?? "javascript"} className="text-white text-xl font-semibold leading-relaxed" />
                </div>

                {/* Back card */}
                <div className="bg-[#1E1F25] border border-indigo-500/30 rounded-2xl p-6 mb-6 shadow-lg">
                    <div className="flex items-center gap-2 text-xs text-indigo-400 mb-3">
                        <Brain size={13} />
                        <span>BACK</span>
                    </div>
                    <CodeBlock text={card.back} lang={card.lang ?? "javascript"} className="text-gray-200 text-base leading-relaxed" />
                </div>

                {/* Hint */}
                {card.hint && (
                    <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-2xl p-5 mb-6 shadow-lg">
                        <div className="text-xs text-gray-500 mb-2">💡 HINT</div>
                        <p className="text-gray-300 text-sm">{card.hint}</p>
                    </div>
                )}

                {/* Tags */}
                {card.tags && (
                    <div className="flex items-center gap-2 flex-wrap mb-6">
                        <Tag size={13} className="text-gray-500" />
                        {card.tags.split(",").map(tag => (
                            <span key={tag} className="text-xs bg-gray-800 text-gray-300 px-2 py-1 rounded-full border border-gray-700">
                                {tag.trim()}
                            </span>
                        ))}
                    </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-xl p-4">
                        <div className="text-xs text-gray-500 mb-1 flex items-center gap-1"><BarChart2 size={12} /> Difficulty</div>
                        <span className="rounded-full bg-gray-700 px-2 py-0.5 text-sm font-medium text-gray-300">{difficultyLabel}</span>
                    </div>
                    <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-xl p-4">
                        <div className="text-xs text-gray-500 mb-1">State</div>
                        <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${stateColor}`}>{card.learning_state}</span>
                    </div>
                    <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-xl p-4">
                        <div className="text-xs text-gray-500 mb-1">Repetitions</div>
                        <span className="text-white text-sm font-medium">{card.number_of_revisions}</span>
                    </div>
                    <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-xl p-4">
                        <div className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Calendar size={12} /> Next Review</div>
                        <span className="text-white text-sm font-medium">
                            {formatNextReview(card.next_revision_date)}
                        </span>
                    </div>
                </div>

                <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-xl p-4 mb-6">
                    <div className="text-xs text-gray-500 mb-1 flex items-center gap-1"><FolderOpen size={12} /> Folder</div>
                    <select
                        value={card.parentFolderId ?? ""}
                        onChange={handleFolderChange}
                        disabled={areFoldersLoading || isUpdating}
                        aria-label="Recall card folder"
                        className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm font-medium text-white outline-none transition focus:border-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <option value="">No Folder</option>
                        {sortedFolders.map(folder => (
                            <option key={folder.id} value={folder.id}>{folder.name}</option>
                        ))}
                    </select>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                    <button
                        onClick={() => navigate(`/recall/review?cards=${id}`)}
                        className="flex-1 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition"
                    >
                        Start Review
                    </button>
                    <button
                        onClick={() => setShowEditModal(true)}
                        className="px-5 py-3 rounded-xl border border-gray-700 text-gray-300 hover:bg-gray-800 text-sm font-medium transition"
                    >
                        Edit
                    </button>
                    {!confirmDelete ? (
                        <button
                            onClick={() => setConfirmDelete(true)}
                            className="px-5 py-3 rounded-xl border border-gray-700 text-gray-400 hover:border-red-500/50 hover:text-red-400 hover:bg-red-500/5 text-sm font-medium transition flex items-center gap-2"
                        >
                            <Trash2 size={15} />
                            Delete
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className="px-4 py-3 rounded-xl bg-red-600 hover:bg-red-500 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium transition flex items-center gap-1.5"
                            >
                                <Trash2 size={14} />
                                {isDeleting ? "Deleting..." : "Confirm"}
                            </button>
                            <button
                                onClick={() => setConfirmDelete(false)}
                                disabled={isDeleting}
                                className="px-4 py-3 rounded-xl border border-gray-700 text-gray-400 hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium transition"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
                {deleteError && (
                    <p className="mt-3 text-sm text-red-400">
                        Could not delete the card. Please try again.
                    </p>
                )}
            </div>
            {showEditModal && (
                <AddRecallCardModal
                    title="Edit Card"
                    initialFront={card.front ?? ""}
                    initialBack={card.back ?? ""}
                    initialHint={card.hint ?? ""}
                    initialTags={card.tags ?? ""}
                    isSubmitting={isUpdating}
                    onClose={() => setShowEditModal(false)}
                    onSubmit={(data) => {
                        updateCard(
                            {
                                recallCardId: id,
                                cardData: { ...card, front: data.title, back: data.deadline, hint: data.repeat, tags: data.tags },
                            },
                            { onSuccess: () => setShowEditModal(false) }
                        );
                    }}
                />
            )}
        </div>
    );
};
